/**
 * Footer — the AMD site footer.
 *
 * Port of the legacy `components/layout/Footer.vue`: social row, five link
 * columns, the pipe-separated legal row, and the copyright line. Content lives
 * in config/footerLinks.js so this file is layout only.
 *
 * Deliberate differences from the Vue original:
 *  - ONE link list at every width. Legacy renders a desktop grid AND a separate
 *    mobile accordion whose lists disagree (Company loses an entry, Partners
 *    gains one and uses a different URL) — a bug, not a feature. The grid here
 *    just wraps.
 *  - Every external link gets target/rel. Legacy applies them inconsistently,
 *    leaving several links without `rel="noopener noreferrer"`.
 *  - No Font Awesome. Seven inline SVGs instead of an icon font (see
 *    socialIcons.jsx).
 *  - Styling is a CSS Module, not inline `style=""` attributes plus a global
 *    unscoped <style> block that also carried header, banner and home-page
 *    rules unrelated to the footer.
 */
import { FOOTER_COLUMNS, FOOTER_LEGAL, FOOTER_SOCIAL } from '@config/footerLinks';
import { cx } from '@lib/classNames';

import { SOCIAL_ICONS } from './socialIcons';

import styles from './Footer.module.css';

/** External links always carry both attributes; in-page ones carry neither. */
function externalProps(external = true) {
  return external ? { target: '_blank', rel: 'noopener noreferrer' } : {};
}

export function Footer({ className }) {
  const year = new Date().getFullYear();

  return (
    <footer className={cx(styles.footer, className)}>
      <div className={styles.inner}>
        <ul className={styles.social}>
          {FOOTER_SOCIAL.map((item) => {
            const Icon = SOCIAL_ICONS[item.icon];
            return (
              <li key={item.id}>
                <a
                  className={styles.socialLink}
                  href={item.href}
                  aria-label={item.label}
                  {...externalProps()}
                >
                  {Icon ? <Icon /> : null}
                </a>
              </li>
            );
          })}
        </ul>

        <div className={styles.columns}>
          {FOOTER_COLUMNS.map((column) => (
            <nav key={column.heading} className={styles.column} aria-label={column.heading}>
              <h2 className={styles.heading}>{column.heading}</h2>
              <ul className={styles.list}>
                {column.links.map((link) => (
                  <li key={link.href}>
                    <a className={styles.link} href={link.href} {...externalProps()}>
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <nav className={styles.legal} aria-label="Legal">
          <ul className={styles.legalList}>
            {FOOTER_LEGAL.map((link, i) => (
              <li key={link.href}>
                {i > 0 ? (
                  <span className={styles.pipe} aria-hidden="true">
                    |
                  </span>
                ) : null}
                <a
                  className={cx(styles.legalLink, link.className)}
                  href={link.href}
                  {...externalProps(link.external)}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <p className={styles.copyright}>© {year} Advanced Micro Devices, Inc.</p>
      </div>
    </footer>
  );
}

export default Footer;
